import { Controller } from '@hotwired/stimulus'
import { useDebounce } from 'stimulus-use'

export default class extends Controller {
  static targets = [
    'imageInput',
    'nameInput',
    'tagsInput',
    'containerInput',
    'mobileInput',
    'excludeSearchInput',
    'loading',
    'preview',
    'placeholder',
    'error',
    'similar',
  ]
  static values = { url: String, similarUrl: String, excludeId: Number }
  static debounces = ['findSimilar']

  connect() {
    useDebounce(this, { wait: 500 })
    this.analyzing = false
    this.currentFile = null
  }

  async analyze(event) {
    const file = event.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/') && !this.isHeic(file)) return

    this.currentFile = file
    this.showLoading()

    if (this.isHeic(file)) {
      await this.analyzeWithServerPreview(file)
    } else {
      this.showPreview(file)
      if (this.nameInputTarget.value.trim() === '') {
        await this.performAnalysis(file)
      } else {
        this.hideLoading()
        await this.findSimilar()
      }
    }
  }

  isHeic(file) {
    return (
      file.type === 'image/heic' ||
      file.type === 'image/heif' ||
      file.name.toLowerCase().endsWith('.heic') ||
      file.name.toLowerCase().endsWith('.heif')
    )
  }

  async analyzeWithServerPreview(file) {
    this.hideError()

    try {
      const formData = new FormData()
      formData.append('image', file)

      const response = await fetch(this.urlValue, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector("[name='csrf-token']").content,
        },
        body: formData,
      })

      if (!response.ok) throw new Error('Analysis failed')

      const result = await response.json()

      if (result.preview) {
        this.previewTarget.src = result.preview
        this.previewTarget.classList.remove('d-none')
        if (this.hasPlaceholderTarget) {
          this.placeholderTarget.classList.add('d-none')
        }
      }

      if (result.error) {
        this.showError(result.error)
        return
      }

      if (result.name && this.nameInputTarget.value.trim() === '') {
        this.nameInputTarget.value = result.name
      }

      this.updateTags(result)
      this.updateOptions(result)
      await this.findSimilar()
    } catch (error) {
      console.error('Image analysis error:', error)
      this.showError('Could not analyze image. Please enter details manually.')
    } finally {
      this.analyzing = false
      this.hideLoading()
    }
  }

  showPreview(file) {
    const reader = new FileReader()
    reader.onload = (e) => {
      if (this.hasPreviewTarget) {
        this.previewTarget.src = e.target.result
        this.previewTarget.classList.remove('d-none')
      }
      if (this.hasPlaceholderTarget) {
        this.placeholderTarget.classList.add('d-none')
      }
    }
    reader.readAsDataURL(file)
  }

  async performAnalysis(file) {
    if (this.analyzing) return
    this.analyzing = true

    this.hideError()

    try {
      const formData = new FormData()
      formData.append('image', file)

      const response = await fetch(this.urlValue, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector("[name='csrf-token']").content,
        },
        body: formData,
      })

      if (!response.ok) throw new Error('Analysis failed')

      const result = await response.json()

      if (result.error) {
        this.showError(result.error)
        return
      }

      if (result.name && this.nameInputTarget.value.trim() === '') {
        this.nameInputTarget.value = result.name
      }

      this.updateTags(result)
      this.updateOptions(result)
      await this.findSimilar()
    } catch (error) {
      console.error('Image analysis error:', error)
      this.showError('Could not analyze image. Please enter details manually.')
    } finally {
      this.analyzing = false
      this.hideLoading()
    }
  }

  updateTags(result) {
    const tagSelectorController =
      this.application.getControllerForElementAndIdentifier(
        this.tagsInputTarget,
        'tag-selector'
      )

    if (tagSelectorController) {
      tagSelectorController.addAISuggestions(
        result.existing_tags || [],
        result.suggested_tags || []
      )
    }
  }

  updateOptions(result) {
    if (this.hasContainerInputTarget && result.is_container !== undefined) {
      this.containerInputTarget.checked = result.is_container
    }
    if (this.hasMobileInputTarget && result.is_mobile !== undefined) {
      this.mobileInputTarget.checked = result.is_mobile
    }
    if (
      this.hasExcludeSearchInputTarget &&
      result.exclude_from_search !== undefined
    ) {
      this.excludeSearchInputTarget.checked = result.exclude_from_search
    }
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove('d-none')
      this.loadingTarget.classList.add('d-flex')
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('d-none')
      this.loadingTarget.classList.remove('d-flex')
    }
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.classList.remove('d-none')
    }
  }

  hideError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add('d-none')
    }
  }

  async findSimilar() {
    const name = this.nameInputTarget.value.trim()
    if (!name && !this.currentFile) {
      this.showEmptyState()
      return
    }

    const formData = new FormData()
    if (name) formData.append('name', name)
    if (this.currentFile) formData.append('image', this.currentFile)
    if (this.excludeIdValue) formData.append('exclude_id', this.excludeIdValue)

    try {
      const response = await fetch(this.similarUrlValue, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector("[name='csrf-token']").content,
        },
        body: formData,
      })

      if (!response.ok) return

      const result = await response.json()
      this.renderSimilar(result.similar)
    } catch (error) {
      console.error('Similar items search error:', error)
    }
  }

  renderSimilar(items) {
    if (!this.hasSimilarTarget) return
    this.renderSimilarList(
      this.similarTarget,
      items,
      'No similar items found'
    )
  }

  renderSimilarList(container, items, emptyMessage) {
    const listEl = container.querySelector('.similar-list')

    if (!items || items.length === 0) {
      listEl.innerHTML = `<small class="text-muted">${emptyMessage}</small>`
      return
    }

    listEl.innerHTML = items
      .map(
        (item) => `
      <div class="d-flex align-items-center gap-2 py-1 border-bottom">
        ${
          item.image_url
            ? `<img src="${item.image_url}" class="rounded" style="width: 32px; height: 32px; object-fit: cover;">`
            : `<div class="rounded bg-light d-flex align-items-center justify-content-center" style="width: 32px; height: 32px;"><i class="bi bi-box text-muted small"></i></div>`
        }
        <div class="flex-grow-1 min-width-0">
          <a href="/things/${item.id}" target="_blank" class="text-decoration-none small d-block text-truncate">
            ${this.escapeHtml(item.name)}
          </a>
          <small class="text-muted text-truncate d-block" style="font-size: 0.7rem;">${this.escapeHtml(item.path)}</small>
        </div>
        <span class="badge bg-secondary" style="font-size: 0.65rem;">
          ${item.similarity}%
        </span>
      </div>
    `
      )
      .join('')
  }

  showEmptyState() {
    if (this.hasSimilarTarget) {
      const listEl = this.similarTarget.querySelector('.similar-list')
      listEl.innerHTML = `<small class="text-muted">Type a name or pick an image to find similar items...</small>`
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
